<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, email as emailRule } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';
import { shortDate } from 'dashboard/components-next/Contacts/contactTime';

// "Editar contacto" side panel of the CrmB mockup: avatar header, two-column
// form (Nombre, Empresa, los atributos sincronizados, Correo, Teléfono) y un
// pie fijo con la nota de historial + Cancelar / Actualizar contacto. Toma el
// contacto en crudo (snake_case) del panel de la conversación y lo guarda de
// una vez. Ciudad, país, biografía y redes no se piden: si el contacto ya los
// tiene guardados se conservan tal cual al actualizar.
const emit = defineEmits(['saved']);

const { t, locale } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSaving = ref(false);
const contact = ref({});

const state = reactive({
  name: '',
  companyName: '',
  // Un valor por cada atributo sincronizado con etiquetas, indexado por su
  // clave: el formulario no conoce cuáles son hasta que carga la cuenta.
  syncedValues: {},
  email: '',
  phoneNumber: '',
});

const rules = {
  name: { required },
  email: { email: emailRule },
};
const v$ = useVuelidate(rules, state);

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const syncedAttributes = computed(() =>
  syncedListAttributes(contactAttributes.value)
);

const subtitle = computed(() =>
  [
    contact.value.name,
    contact.value.created_at
      ? t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.CREATED', {
          date: shortDate(contact.value.created_at, locale.value),
        })
      : '',
  ]
    .filter(Boolean)
    .join(' · ')
);

const open = record => {
  contact.value = record || {};
  const additional = record?.additional_attributes || {};
  const custom = record?.custom_attributes || {};
  Object.assign(state, {
    name: record?.name || '',
    companyName: record?.company?.name || additional.company_name || '',
    syncedValues: Object.fromEntries(
      syncedAttributes.value.map(attribute => [
        attribute.attributeKey,
        custom[attribute.attributeKey] || '',
      ])
    ),
    email: record?.email || '',
    phoneNumber: record?.phone_number || '',
  });
  v$.value.$reset();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const save = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;
  isSaving.value = true;
  const customAttributes = { ...(contact.value.custom_attributes || {}) };
  syncedAttributes.value.forEach(attribute => {
    customAttributes[attribute.attributeKey] =
      state.syncedValues[attribute.attributeKey] || null;
  });
  try {
    await store.dispatch('contacts/update', {
      id: contact.value.id,
      name: state.name.trim(),
      email: state.email.trim(),
      phoneNumber: state.phoneNumber,
      additionalAttributes: {
        ...(contact.value.additional_attributes || {}),
        companyName: state.companyName.trim(),
      },
      customAttributes,
    });
    useAlert(t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.SUCCESS'));
    emit('saved');
    close();
  } catch (error) {
    useAlert(
      error?.message || t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, close });

const LABEL_CLASS = 'text-[11.5px] font-semibold text-n-slate-11';
const INPUT_CLASS =
  'w-full h-[34px] px-2.5 rounded-lg border border-n-weak bg-n-alpha-1 text-[13px] text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:bg-n-solid-2 focus:border-n-brand/60 transition-colors reset-base';
const SELECT_CLASS =
  'w-full h-[34px] px-2 rounded-lg border border-n-weak bg-n-alpha-1 text-[13px] text-n-slate-12 outline-none focus:bg-n-solid-2 focus:border-n-brand/60 transition-colors';
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    flush
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col h-full min-h-0">
      <!-- header -->
      <div
        class="flex items-center gap-3 px-5 pt-[18px] pb-3.5 border-b border-n-weak"
      >
        <Avatar
          :name="contact.name || ''"
          :src="contact.thumbnail || ''"
          :size="36"
        />
        <div class="flex flex-col min-w-0 gap-0.5">
          <span
            class="text-[15px] font-semibold tracking-tight text-n-slate-12"
          >
            {{ t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.TITLE') }}
          </span>
          <span class="text-xs truncate text-n-slate-10">{{ subtitle }}</span>
        </div>
        <button
          type="button"
          class="flex items-center justify-center ml-auto size-7 rounded-[7px] text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
          @click="close"
        >
          <Icon icon="i-lucide-x" size="size-3.5" />
        </button>
      </div>

      <!-- body -->
      <div
        class="flex flex-col flex-1 min-h-0 gap-4 px-5 py-[18px] overflow-y-auto"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-3.5 gap-y-3">
          <label class="flex flex-col gap-1.5">
            <span :class="LABEL_CLASS">{{
              t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.NAME')
            }}</span>
            <input
              v-model="state.name"
              :class="[INPUT_CLASS, v$.name.$error ? '!border-n-ruby-8' : '']"
              :placeholder="
                t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.NAME_PLACEHOLDER')
              "
              @blur="v$.name.$touch()"
            />
            <span v-if="v$.name.$error" class="text-xs text-n-ruby-11">
              {{ t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.NAME_REQUIRED') }}
            </span>
          </label>
          <label class="flex flex-col gap-1.5">
            <span :class="LABEL_CLASS">{{
              t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.COMPANY')
            }}</span>
            <input
              v-model="state.companyName"
              :class="INPUT_CLASS"
              :placeholder="
                t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.COMPANY_PLACEHOLDER')
              "
            />
          </label>
          <label
            v-for="attribute in syncedAttributes"
            :key="attribute.id"
            class="flex flex-col gap-1.5"
          >
            <span :class="LABEL_CLASS">
              {{ attribute.attributeDisplayName }}
            </span>
            <select
              v-model="state.syncedValues[attribute.attributeKey]"
              :class="SELECT_CLASS"
            >
              <option value="">
                {{ t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.NONE') }}
              </option>
              <option
                v-for="value in attribute.attributeValues || []"
                :key="value"
                :value="value"
              >
                {{ value }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1.5">
            <span :class="LABEL_CLASS">{{
              t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.EMAIL')
            }}</span>
            <input
              v-model="state.email"
              type="email"
              :class="[INPUT_CLASS, v$.email.$error ? '!border-n-ruby-8' : '']"
              :placeholder="
                t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.EMAIL_PLACEHOLDER')
              "
              @blur="v$.email.$touch()"
            />
            <span v-if="v$.email.$error" class="text-xs text-n-ruby-11">
              {{ t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.EMAIL_INVALID') }}
            </span>
          </label>
          <div class="flex flex-col gap-1.5">
            <span :class="LABEL_CLASS">{{
              t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.PHONE')
            }}</span>
            <PhoneNumberInput
              v-model="state.phoneNumber"
              placeholder="933 184 191"
            />
          </div>
        </div>
      </div>

      <!-- footer -->
      <div
        class="flex items-center flex-shrink-0 gap-2.5 px-5 pt-3.5 pb-[18px] border-t border-n-weak"
      >
        <span class="text-[11.5px] leading-snug text-n-slate-10 max-w-[150px]">
          {{ t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.HINT') }}
        </span>
        <Button
          type="button"
          :label="t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.CANCEL')"
          color="slate"
          variant="outline"
          size="sm"
          class="ml-auto h-[34px]"
          @click="close"
        />
        <Button
          type="button"
          :label="t('CONVERSATION_SIDEBAR.RAIL.EDIT_DIALOG.SAVE')"
          size="sm"
          class="h-[34px]"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>
    </div>
  </Dialog>
</template>
